import React from 'react';
import { Layers, Database, Share2, BarChart3, Plus, X, Link, Settings } from 'lucide-react';

const CdsWizard = ({ data = {}, setData }) => {

  const handleToggle = (option) => {
    setData({ ...data, cdsOptions: { ...data.cdsOptions, [option]: !data.cdsOptions[option] } });
  };

  // Associations
  const agregarAssociation = () => {
    const assoc = {
      id: Date.now(),
      nombre: '',
      targetCDS: '',
      cardinalidad: '[0..1]',
      campoLocal: '',
      campoTarget: '',
      tipo: 'padre',
    };
    setData({ ...data, cdsAssociations: [...(data.cdsAssociations || []), assoc] });
  };

  const actualizarAssociation = (id, campo, valor) => {
    setData({
      ...data,
      cdsAssociations: (data.cdsAssociations || []).map(a =>
        a.id === id ? { ...a, [campo]: valor } : a
      ),
    });
  };

  const eliminarAssociation = (id) => {
    setData({ ...data, cdsAssociations: (data.cdsAssociations || []).filter(a => a.id !== id) });
  };

  // Parámetros
  const agregarParametro = () => {
    const param = { id: Date.now(), nombre: '', tipo: 'abap.char(3)', default: '' };
    setData({ ...data, cdsParametros: [...(data.cdsParametros || []), param] });
  };

  const actualizarParametro = (id, campo, valor) => {
    setData({
      ...data,
      cdsParametros: (data.cdsParametros || []).map(p =>
        p.id === id ? { ...p, [campo]: valor } : p
      ),
    });
  };

  const eliminarParametro = (id) => {
    setData({ ...data, cdsParametros: (data.cdsParametros || []).filter(p => p.id !== id) });
  };

  // JOINs adicionales
  const agregarJoin = () => {
    const join = { id: Date.now(), tipo: 'INNER JOIN', tabla: '', alias: '', on: '' };
    setData({ ...data, cdsJoins: [...(data.cdsJoins || []), join] });
  };

  const actualizarJoin = (id, campo, valor) => {
    setData({
      ...data,
      cdsJoins: (data.cdsJoins || []).map(j =>
        j.id === id ? { ...j, [campo]: valor } : j
      ),
    });
  };

  const eliminarJoin = (id) => {
    setData({ ...data, cdsJoins: (data.cdsJoins || []).filter(j => j.id !== id) });
  };

  const inputClass = "w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono uppercase outline-none focus:border-indigo-400";
  const selectClass = "w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-white";
  const labelClass = "text-[9px] font-bold text-slate-400 uppercase block mb-1";

  return (
    <div className="animate-in fade-in slide-in-from-top-4 duration-500 text-left space-y-6">

      {/* NOTA DEL ARQUITECTO */}
      <div className="bg-indigo-900/10 border-l-4 border-indigo-600 p-5 rounded-r-3xl flex gap-4 items-start">
        <div className="bg-indigo-600 p-2 rounded-xl text-white shrink-0"><Layers size={18} /></div>
        <div>
          <p className="text-[11px] text-indigo-400 font-black uppercase tracking-widest mb-1">CDS Modeling Guide</p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Genera DDL completo para Core Data Services S/4HANA. Configurá la entidad, associations, joins, parámetros y anotaciones. El motor genera la vista lista para activar en ADT/Eclipse.
          </p>
        </div>
      </div>

      {/* FILA 1 — DDL BASE + SERVICIOS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* DDL SOURCE */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-sky-500 space-y-4">
          <label className="text-[10px] font-black text-sky-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Database size={14} /> DDL Source & Identificadores
          </label>

          <div>
            <label className={labelClass}>Nombre Entidad CDS (Z_I_...)</label>
            <input type="text" className={inputClass} placeholder="Z_I_SalesOrder"
              value={data.cdsName || ''} onChange={e => setData({ ...data, cdsName: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Nombre Vista SQL (SE11 — máx 16 chars)</label>
            <input type="text" className={inputClass} placeholder="ZSALESORD_SQL"
              value={data.sqlViewName || ''} onChange={e => setData({ ...data, sqlViewName: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Tabla / CDS Fuente Principal</label>
            <input type="text" className={inputClass} placeholder="VBAK o ZI_SalesOrderBase"
              value={data.cdsFuente || ''} onChange={e => setData({ ...data, cdsFuente: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Tipo de Vista VDM</label>
            <select className={selectClass} value={data.cdsVdmType || '#BASIC'}
              onChange={e => setData({ ...data, cdsVdmType: e.target.value })}>
              <option value="#BASIC">#BASIC — Lee de tablas físicas</option>
              <option value="#COMPOSITE">#COMPOSITE — Une vistas básicas</option>
              <option value="#CONSUMPTION">#CONSUMPTION — Expone a OData/Fiori</option>
              <option value="#ANALYTICAL_QUERY">#ANALYTICAL_QUERY — Analytics/KPIs</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Data Category</label>
            <select className={selectClass} value={data.cdsCategory || '#FACT'}
              onChange={e => setData({ ...data, cdsCategory: e.target.value })}>
              <option value="#FACT">#FACT — Transaccional</option>
              <option value="#DIMENSION">#DIMENSION — Maestro</option>
              <option value="#CUBE">#CUBE — Analítico</option>
              <option value="#NONE">#NONE — Sin categoría</option>
            </select>
          </div>
        </div>

        {/* SERVICIOS + ANOTACIONES */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-sky-500 space-y-4">
          <label className="text-[10px] font-black text-sky-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Settings size={14} /> Anotaciones & Servicios
          </label>

          {/* OData */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <Share2 className="text-sky-400 shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">OData Exposure</p>
              <p className="text-[9px] text-slate-400">@OData.publish: true — expone como servicio Fiori</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.cdsOptions?.odata || false} onChange={() => handleToggle('odata')} />
          </label>

          {/* Analytics */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <BarChart3 className="text-emerald-400 shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">Analytics Engine</p>
              <p className="text-[9px] text-slate-400">@Analytics.dataCategory — motor analítico SAP</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.cdsOptions?.analytics || false} onChange={() => handleToggle('analytics')} />
          </label>

          {/* Search */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <Database className="text-amber-400 shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">Search Enabled</p>
              <p className="text-[9px] text-slate-400">@Search.searchable: true — habilita búsqueda semántica</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.cdsOptions?.search || false} onChange={() => handleToggle('search')} />
          </label>

          {/* Metadata Extension */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <Layers className="text-violet-400 shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">Generar Metadata Extension</p>
              <p className="text-[9px] text-slate-400">Objeto separado con anotaciones @UI sin tocar la vista base</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.cdsOptions?.metadataExt || false} onChange={() => handleToggle('metadataExt')} />
          </label>

          {/* Full Stack (Interface + Consumption) */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <Database className="text-cyan-400 shrink-0" size={16} />
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">Generar Interface + Consumption</p>
              <p className="text-[9px] text-slate-400">Genera ZI_ (básica) + ZC_ (consumo) en un solo output</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.cdsOptions?.fullStack || false} onChange={() => handleToggle('fullStack')} />
          </label>

          {/* Access Control */}
          <div>
            <label className={labelClass}>Authorization Check</label>
            <select className={selectClass} value={data.cdsAuthCheck || '#NOT_REQUIRED'}
              onChange={e => setData({ ...data, cdsAuthCheck: e.target.value })}>
              <option value="#NOT_REQUIRED">#NOT_REQUIRED — Sin verificación</option>
              <option value="#CHECK">#CHECK — Verificación obligatoria</option>
              <option value="#PRIVILEGED_ONLY">#PRIVILEGED_ONLY — Solo usuarios privilegiados</option>
            </select>
          </div>

          {/* UI Header Info */}
          <div>
            <label className={labelClass}>@UI.headerInfo — Título (campo clave)</label>
            <input type="text" className={inputClass} placeholder="SalesOrder"
              value={data.cdsHeaderTitle || ''} onChange={e => setData({ ...data, cdsHeaderTitle: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>@UI.headerInfo — Descripción (campo label)</label>
            <input type="text" className={inputClass} placeholder="CustomerName"
              value={data.cdsHeaderDesc || ''} onChange={e => setData({ ...data, cdsHeaderDesc: e.target.value })} />
          </div>
        </div>
      </div>

      {/* ASSOCIATIONS */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-violet-500">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <label className="text-[10px] font-black text-violet-400 uppercase flex items-center gap-2">
            <Link size={14} /> Associations
          </label>
          <button onClick={agregarAssociation}
            className="flex items-center gap-2 px-3 py-1.5 bg-violet-600/20 border border-violet-500/30 rounded-xl text-violet-400 text-[10px] font-black uppercase hover:bg-violet-600/30 transition-all">
            <Plus size={12} /> Agregar
          </button>
        </div>

        {(data.cdsAssociations || []).length === 0 ? (
          <p className="text-[10px] text-slate-600 italic text-center py-4">No hay associations definidas — hacé click en Agregar</p>
        ) : (
          <div className="space-y-4">
            {(data.cdsAssociations || []).map((assoc) => (
              <div key={assoc.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black text-violet-400 uppercase">Association</span>
                  <button onClick={() => eliminarAssociation(assoc.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Nombre (_Supplier)</label>
                    <input type="text" className={inputClass} placeholder="_Supplier"
                      value={assoc.nombre} onChange={e => actualizarAssociation(assoc.id, 'nombre', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>CDS Target (existente)</label>
                    <input type="text" className={inputClass} placeholder="ZI_SupplierMaster"
                      value={assoc.targetCDS} onChange={e => actualizarAssociation(assoc.id, 'targetCDS', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Cardinalidad</label>
                    <select className={selectClass} value={assoc.cardinalidad}
                      onChange={e => actualizarAssociation(assoc.id, 'cardinalidad', e.target.value)}>
                      <option value="[0..1]">[0..1] — Cero o uno</option>
                      <option value="[1..1]">[1..1] — Exactamente uno</option>
                      <option value="[0..*]">[0..*] — Cero o muchos</option>
                      <option value="[1..*]">[1..*] — Uno o muchos</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Tipo</label>
                    <select className={selectClass} value={assoc.tipo}
                      onChange={e => actualizarAssociation(assoc.id, 'tipo', e.target.value)}>
                      <option value="padre">Padre (se llama desde aquí)</option>
                      <option value="hijo">Hijo (este es el hijo)</option>
                      <option value="texto">Texto (tabla de textos)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Campo Local (ON)</label>
                    <input type="text" className={inputClass} placeholder="LifnrId"
                      value={assoc.campoLocal} onChange={e => actualizarAssociation(assoc.id, 'campoLocal', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Campo Target (ON)</label>
                    <input type="text" className={inputClass} placeholder="SupplierId"
                      value={assoc.campoTarget} onChange={e => actualizarAssociation(assoc.id, 'campoTarget', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* JOINS ADICIONALES */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-emerald-500">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <label className="text-[10px] font-black text-emerald-400 uppercase flex items-center gap-2">
            <Database size={14} /> JOINs Adicionales
          </label>
          <button onClick={agregarJoin}
            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600/20 border border-emerald-500/30 rounded-xl text-emerald-400 text-[10px] font-black uppercase hover:bg-emerald-600/30 transition-all">
            <Plus size={12} /> Agregar
          </button>
        </div>

        {(data.cdsJoins || []).length === 0 ? (
          <p className="text-[10px] text-slate-600 italic text-center py-4">No hay JOINs adicionales — hacé click en Agregar</p>
        ) : (
          <div className="space-y-4">
            {(data.cdsJoins || []).map((join) => (
              <div key={join.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-black text-emerald-400 uppercase">JOIN</span>
                  <button onClick={() => eliminarJoin(join.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Tipo de JOIN</label>
                    <select className={selectClass} value={join.tipo}
                      onChange={e => actualizarJoin(join.id, 'tipo', e.target.value)}>
                      <option value="INNER JOIN">INNER JOIN</option>
                      <option value="LEFT OUTER JOIN">LEFT OUTER JOIN</option>
                      <option value="RIGHT OUTER JOIN">RIGHT OUTER JOIN</option>
                      <option value="CROSS JOIN">CROSS JOIN</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Tabla o CDS</label>
                    <input type="text" className={inputClass} placeholder="LFA1 o ZI_SupplierBase"
                      value={join.tabla} onChange={e => actualizarJoin(join.id, 'tabla', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Alias</label>
                    <input type="text" className={inputClass} placeholder="supplier"
                      value={join.alias} onChange={e => actualizarJoin(join.id, 'alias', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Condición ON</label>
                    <input type="text" className={inputClass} placeholder="lifnr = $projection.LifnrId"
                      value={join.on} onChange={e => actualizarJoin(join.id, 'on', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PARÁMETROS WITH PARAMETERS */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-amber-500">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <label className="text-[10px] font-black text-amber-400 uppercase flex items-center gap-2">
            <Settings size={14} /> WITH PARAMETERS
          </label>
          <button onClick={agregarParametro}
            className="flex items-center gap-2 px-3 py-1.5 bg-amber-600/20 border border-amber-500/30 rounded-xl text-amber-400 text-[10px] font-black uppercase hover:bg-amber-600/30 transition-all">
            <Plus size={12} /> Agregar
          </button>
        </div>

        {(data.cdsParametros || []).length === 0 ? (
          <p className="text-[10px] text-slate-600 italic text-center py-4">No hay parámetros — hacé click en Agregar</p>
        ) : (
          <div className="space-y-3">
            {(data.cdsParametros || []).map((param) => (
              <div key={param.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-black text-amber-400 uppercase">Parámetro</span>
                  <button onClick={() => eliminarParametro(param.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Nombre</label>
                    <input type="text" className={inputClass} placeholder="p_language"
                      value={param.nombre} onChange={e => actualizarParametro(param.id, 'nombre', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Tipo ABAP</label>
                    <select className={selectClass} value={param.tipo}
                      onChange={e => actualizarParametro(param.id, 'tipo', e.target.value)}>
                      <option value="abap.char(3)">abap.char(3)</option>
                      <option value="abap.char(4)">abap.char(4)</option>
                      <option value="abap.char(10)">abap.char(10)</option>
                      <option value="abap.lang">abap.lang</option>
                      <option value="abap.dats">abap.dats</option>
                      <option value="abap.numc(4)">abap.numc(4)</option>
                      <option value="abap.cuky">abap.cuky</option>
                      <option value="bukrs">bukrs</option>
                      <option value="gjahr">gjahr</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Valor Default</label>
                    <input type="text" className={inputClass} placeholder="'EN'"
                      value={param.default} onChange={e => actualizarParametro(param.id, 'default', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CAMPOS CALCULADOS Y EXTRAS */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-cyan-500 space-y-4">
        <label className="text-[10px] font-black text-cyan-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
          <BarChart3 size={14} /> Opciones Avanzadas
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Campos Calculados / Expresiones</label>
            <textarea className="w-full p-3 bg-slate-800 rounded-xl text-[11px] font-mono text-white placeholder:text-slate-600 outline-none resize-none h-20"
              placeholder={"CASE WHEN status = 'A' THEN 'Activo' ELSE 'Inactivo' END as StatusText,\nCONCAT(firstname, ' ', lastname) as FullName"}
              value={data.cdsCamposCalculados || ''}
              onChange={e => setData({ ...data, cdsCamposCalculados: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>WHERE / Filtros Fijos</label>
            <textarea className="w-full p-3 bg-slate-800 rounded-xl text-[11px] font-mono text-white placeholder:text-slate-600 outline-none resize-none h-20"
              placeholder={"mandt = $session.client\nAND spras = $session.system_language"}
              value={data.cdsFiltros || ''}
              onChange={e => setData({ ...data, cdsFiltros: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>GROUP BY (campos para agregación)</label>
            <input type="text" className={inputClass} placeholder="bukrs, gjahr, kostl"
              value={data.cdsGroupBy || ''} onChange={e => setData({ ...data, cdsGroupBy: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Campos de proyección de Associations</label>
            <input type="text" className={inputClass} placeholder="_Supplier, _Material, _CompanyCode"
              value={data.cdsProyeccion || ''} onChange={e => setData({ ...data, cdsProyeccion: e.target.value })} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default CdsWizard; 